import { DatePipe, Location } from "@angular/common";
import { Component } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDivider } from "@angular/material/divider";
import { MatIcon } from "@angular/material/icon";
import { MatProgressBar } from '@angular/material/progress-bar';
import { ActivatedRoute, Router } from "@angular/router";
import { Doctor } from "@interfaces/doctors";
import { DoctorsService } from "@services/doctors/doctors.service";
import { ReportService } from '@services/report/report.service';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzUploadChangeParam, NzUploadModule } from 'ng-zorro-antd/upload';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-patient-reports',
  imports: [
    MatIcon,
    MatDivider,
    MatProgressBar,
    MatButton,
    DatePipe,
    NzModalModule,
    NzIconModule,
    NzUploadModule,
    NzDividerModule,
    NzTableModule,
    NzButtonModule,
    NzPopconfirmModule
  ],
  templateUrl: './patient-reports.component.html',
  standalone: true,
  styleUrl: './patient-reports.component.scss'
})
export class PatientReportsComponent {
  reports?: any[];
  doctors?: Doctor[];

  uploadPdfModalVisible = false;
  isUploading = false;

  urlToUpload = 'https://api.example.com/upload';
  patientId = '';
  doctorId = '';

  constructor(
    private doctorsService: DoctorsService,
    private reportsService: ReportService,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: NzMessageService
  ) {
    this.doctorsService.doctorsListObservable$
      .subscribe((doctors: any) => {
        this.doctors = doctors;
      });

    this.route.paramMap.subscribe(params => {
      this.doctorId = params.get('doctorId') ?? '';
      this.patientId = params.get('patientId') ?? '';
      if (this.patientId) {
        this.loadReports(this.doctorId, this.patientId);
        this.urlToUpload = this.reportsService.urlToUploadPdf(this.patientId);
      }
    });
  }

  handleChange(info: NzUploadChangeParam): void {
    if (info.file.status === 'uploading') {
      this.isUploading = true;
    } else {
      this.isUploading = false;
    }
    if (info.file.status !== 'uploading') {
      console.log(info.file, info.fileList);
    }
    if (info.file.status === 'done') {
      this.messageService.success(`${info.file.name} file uploaded successfully`);
      // Refresh the reports after successful upload
      this.loadReports(this.doctorId, this.patientId);

    } else if (info.file.status === 'error') {
      this.messageService.error(`${info.file.name} file upload failed.`);
    }
  }

  loadReports(doctorId: string, patientId: string) {
    if (patientId) {
      this.reportsService.updateReportsList([]);
      this.reports = [];

      forkJoin({
        reports: this.reportsService.fetchReportDetail(doctorId, patientId),
        pdfReports: this.reportsService.fetchPdfReportDetail(doctorId, patientId)
      }).subscribe(({ reports, pdfReports }) => {
        const mappedPdfReports = (pdfReports || []).map((report: any) => ({
          ...report,
          status: 'PDF',
          type: 'PDF',
          id: report.id || report.filename,
          updated_at: report.updated_at || report.created_at,
        }));

        // Combine both types of reports
        this.reports = [...(reports || []), ...mappedPdfReports];

        // Sort reports by updated_at date in descending order
        this.reports.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

        // Update the reports list in the service
        this.reportsService.updateReportsList(this.reports);
      });
    }
  }

  goBack() {
    this.location.back();
  }

  openReport(report: any) {
    if (report.type === 'PDF') {
      this.openPdfReport(report);
      return;
    }
    this.router.navigate([`${report.id}`], { relativeTo: this.route });
  }

  openPdfReport(report: any) {
    // open pdf in new tab
    const pdfUrl = report.pdf_url || report.url;
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    } else {
      console.error('PDF URL not found for report:', report);
    }
  }

  createReport() {
    this.router.navigate([`create`], { relativeTo: this.route });
  }

  uploadPdf() {
    this.uploadPdfModalVisible = true;
  }

  handleCancel() {
    this.uploadPdfModalVisible = false;
  }

  deleteReportAndReload(reportId: string): void {
    this.reportsService.deleteReport(reportId).subscribe({
      next: () => {
        this.messageService.success('Report deleted successfully');
        this.loadReports(this.doctorId, this.patientId);
      },
      error: (error) => {
        this.messageService.error('Failed to delete the report');
      }
    });
  }

  cancel(): void {
    this.messageService.info('Deletion cancelled');
  }

}
